import useSWR from "swr";
import type { ProductSummary } from "@/common/productSummary/types";
import { useMonarchRule } from "../MonarchRulesProvider";
import {
  HomepageOptimizationV3TestData,
  UseHomepageOptimizationV3,
} from "./types";

const CUSTOM_RULE_ID = "homepageOptimizationTestV3";

const fetchProductSummaries = async (url: string, uuids: string[]) => {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uuids }),
  });
  if (!res.ok) throw new Error("Failed to fetch product summaries");
  return res.json();
};

const useHomepageOptimizationV3: UseHomepageOptimizationV3 = () => {
  const { isLoading: isMonarchLoading, value } = useMonarchRule<{
    data: HomepageOptimizationV3TestData;
  }>(CUSTOM_RULE_ID, {});

  const productUuids: string[] = [];
  value?.data?.sections?.forEach((section) => {
    if (section.productCardList) {
      section.productCardList.forEach((card) =>
        productUuids.push(card.productUuid),
      );
    }
  });

  const { data: productSummaries, isLoading: isProductSummariesLoading } =
    useSWR(
      productUuids.length > 0 ? ["/api/product-summaries", productUuids] : null,
      ([url, uuids]) => fetchProductSummaries(url, uuids),
    );

  const transformedSections =
    value?.data?.sections?.map((section) => {
      if (!productSummaries || !section.productCardList) {
        return section;
      }

      const products = section.productCardList
        .map((card) => {
          const summary = productSummaries.find(
            (s: ProductSummary) => s.productUuid === card.productUuid,
          );
          if (!summary) return null;

          // Merge productCardList metadata (productBadges, isNew) with ProductSummary
          return {
            ...summary,
            position: card.position,
            productBadges: card.productBadges,
            isNew: card.isNew,
          };
        })
        .filter((summary) => summary !== null)
        .sort((a, b) => a.position - b.position);

      return { ...section, products };
    }) ?? [];

  const isLoading = isMonarchLoading || isProductSummariesLoading;

  if (isLoading || !value) {
    return {
      isLoading,
      data: {
        variant: "control",
      },
    };
  }

  const variant =
    value?.data?.sections?.length === 0
      ? "control"
      : (value.data.variant ?? "control");

  return {
    isLoading,
    variant,
    data: {
      pageTitle: value?.data?.pageTitle ?? "",
      carouselAutoplay: value?.data?.carouselAutoplay,
      carouselAutoplayDelay: value?.data?.carouselAutoplayDelay,
      sections: transformedSections,
    },
  };
};

export default useHomepageOptimizationV3;
